import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Truck,
  ShieldCheck,
  Package,
  CircleCheck,
  MapPin,
  Scissors,
  Recycle,
  Tag,
  Layers,
  Boxes,
  Sparkles,
  Calendar,
  Lock,
  CreditCard,
} from "lucide-react";
import { fetchProducts } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { getCityPageBySlug } from "@/data/citiesContent";

const cityNameToSlug = (name) =>
  name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/'/g, "")
    .replace(/\s+/g, "-");

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison Sèvres + 4 engagements ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Livraison directe
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Livraison de vos cartons de déménagement à {city.name}
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Oubliez la contrainte des déplacements dans les enseignes de
            bricolage et la manutention fastidieuse de liasses de cartons
            volumineuses. Nous prenons en main tout le circuit de transport
            jusqu&apos;à votre adresse à {city.name}. Notre service de
            distribution se distingue par des engagements stricts.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            {
              title: "Acheminement par notre propre équipe",
              body: "Nous excluons totalement les transporteurs tiers et les plateformes de messagerie impersonnelles. C'est notre propre structure qui charge et dépose vos fournitures, vous garantissant une manipulation soignée et un respect absolu de votre matériel.",
              Icon: Truck,
            },
            {
              title: "Service disponible en Île-de-France",
              body: "Notre société opère sur un réseau de proximité pour assurer des flux directs et rapides à l'échelle régionale, exclusivement en Île-de-France.",
              Icon: MapPin,
            },
            {
              title: "Créneau horaire défini avec le client",
              body: "Pas d'attente interminable. Nous fixons ensemble une plage horaire précise et adaptée à votre emploi du temps pour une organisation sans faille.",
              Icon: Calendar,
            },
            {
              title: "Livraison à domicile ou en entreprise",
              body: "Que vous soyez un particulier ou un professionnel, nos équipes déposent vos commandes directement à votre porte ou au sein de vos bureaux, vous évitant tout effort de manutention.",
              Icon: Package,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
            >
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b]">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Packs tout-en-un + Kit T3 ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Packs tout-en-un
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Découvrez nos packs tout-en-un et facilitez vos préparatifs
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Pour vous éviter des calculs complexes ou le stress d&apos;oublier
            l&apos;essentiel, nos spécialistes ont conçu des configurations
            globales calibrées selon vos besoins réels. Sélectionnez la taille
            de votre espace et recevez un écosystème complet.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            {
              title: "Des dotations sur mesure",
              body: "Du studio à la maison familiale, nos kits intègrent le volume idéal de cartons standards, de boîtes à livres compactes, de rouleaux d'adhésif technique et de film bulle protecteur pour armer vos préparatifs.",
              Icon: Boxes,
            },
            {
              title: "La simplicité avant tout",
              body: "En choisissant un pack complet, vous recevez en une seule fois tout le matériel nécessaire pour sectoriser et emballer vos pièces, sans risquer de tomber en panne de fournitures au milieu de la nuit.",
              Icon: Sparkles,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-[#EEF3F7] rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
            >
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[16px] text-[#0f1d2b]">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>

        {/* Exemple Kit T3 */}
        <div className="mt-8 bg-[#0f1d2b] text-white rounded-3xl p-6 md:p-8">
          <div className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center justify-center h-10 px-3 rounded-lg bg-[#FF7A00] text-white text-[13px] font-extrabold tracking-wide">
              Exemple — Kit T3
            </span>
            <span className="text-[13.5px] text-white/70 font-medium">
              Appartement 3 pièces — 55 à 75 m²
            </span>
          </div>
          <ul className="mt-5 space-y-3">
            {[
              "60 cartons standards : le gros volume indispensable pour emballer le quotidien de toute la famille.",
              "30 cartons livres : costauds et parfaits pour vos collections de BD, romans, archives ou objets lourds.",
              "1 grand rouleau de papier bulle (0,5 m × 100 m) : 100 mètres de long ! La sécurité ultime et ultra-généreuse pour toute votre vaisselle et vos bibelots.",
              "6 rouleaux d'adhésif pro : le stock parfait pour scotcher à la chaîne comme un vrai pro de la manutention.",
            ].map((item, i) => (
              <li
                key={i}
                className="flex items-start gap-3 text-[14.5px] text-white/90 leading-relaxed"
              >
                <CircleCheck
                  className="h-5 w-5 text-[#FF7A00] mt-0.5 shrink-0"
                  strokeWidth={2.4}
                />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <Link
            to="/packs-cartons"
            className="mt-7 inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#FF7A00] hover:bg-[#E66E00] text-white font-bold text-[14px] transition-colors"
            data-testid="city-sevres-cta-kits"
          >
            <Layers className="h-4 w-4" strokeWidth={2.6} />
            Découvrez nos autres kits (T1, T2 et le kit Maison)
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
        </div>
      </div>
    </section>

    {/* ======= SECTION 4 — Pourquoi choisir nos cartons (3 avantages) ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Nos garanties qualité
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Pourquoi choisir nos cartons de déménagement&nbsp;?
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Faire le choix de notre catalogue, c&apos;est opter pour une
            rigueur industrielle pensée pour éliminer le stress de la mise en
            boîte grâce à des critères de fabrication stricts.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            {
              title: "Robustesse structurelle supérieure",
              body: "Nos cartons possèdent une épaisseur de cannelure renforcée qui évite l'affaissement. Vous pouvez les superposer sur plusieurs niveaux dans le véhicule de transport sans redouter l'écrasement des modules inférieurs.",
              Icon: ShieldCheck,
            },
            {
              title: "Formats standardisés",
              body: "L'intégralité de nos boîtes est calibrée pour s'emboîter parfaitement. Cela permet de créer des blocs stables et solidarisés dans le camion de déménagement, évitant les glissements et les chutes lors des virages.",
              Icon: Boxes,
            },
            {
              title: "Filière éco-conçue",
              body: "Fabriqués à partir de matières premières végétales issues de filières responsables, nos contenants sont totalement recyclables et hautement réutilisables, limitant l'impact écologique de votre transition.",
              Icon: Recycle,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5"
            >
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b]">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-sevres-cta-shop"
          >
            Voir tous les cartons
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/packs-cartons"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-sevres-cta-kits-bottom"
          >
            Choisir un kit
          </Link>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — Flotte 92 ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Couverture {city.deptCode}
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Vos fournitures et déménagements gérés aux quatre coins du
            {" "}{city.deptCode}
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Que votre projet se passe à deux pas d&apos;ici ou que vous vous
            installiez ailleurs dans les {city.deptName}, notre flotte
            logistique quadrille le secteur pour vous approvisionner en
            cartons professionnels et sécuriser votre mobilier. Voici les
            communes du {city.deptCode} dans lesquelles nos équipes sont les
            plus actives&nbsp;:
          </p>
        </div>

        <ul className="mt-6 flex flex-wrap gap-x-5 gap-y-2.5 text-[15px] md:text-base">
          {city.nearbyCities.map((name) => {
            const idSlug = cityNameToSlug(name);
            const target = getCityPageBySlug(idSlug);
            return (
              <li key={name}>
                {target ? (
                  <Link
                    to={`/carton-demenagement/${target.slug}`}
                    className="inline-flex items-center font-semibold text-[#237cb5] underline underline-offset-4 decoration-[#237cb5]/40 hover:decoration-[#237cb5] hover:text-[#0f1d2b] transition-colors"
                    data-testid={`city-nearby-${idSlug}`}
                  >
                    Carton déménagement {name}
                  </Link>
                ) : (
                  <span
                    className="inline-flex items-center font-semibold text-[#5b6b7a]"
                    data-testid={`city-nearby-${idSlug}`}
                  >
                    Carton déménagement {name}
                  </span>
                )}
              </li>
            );
          })}
        </ul>

        <div className="mt-8">
          <Link
            to={`/cartons-demenagement/${city.deptSlug}`}
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-sevres-cta-back-dept"
          >
            <Package className="h-4 w-4" strokeWidth={2.6} />
            Voir la page {city.deptName} ({city.deptCode})
          </Link>
        </div>
      </div>
    </section>
  </>
);

export default { Sections };
